import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { AppleLogo } from "@/assets/logos/AppleLogo";
import { AuthenticationModal } from "@/components/environments/onboarding/CreateProjectFromEnvironmentModal";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import {
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { Tooltip } from "@/components/Tooltip";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { TrackLocations } from "@/hooks/use-segment";
import { toPlainRunnerEnvironmentClass, useRunnerEnvironments } from "@/queries/environment-queries";
import {
    useDeleteHostAuthenticationToken,
    useListAllRunnerSCMIntegrations,
    useListHostAuthenticationTokens,
} from "@/queries/runner-configuration-queries";
import {
    useIsUserAuthenticatedWithRunner,
    useListRunners,
    type AuthenticatedWithRunnerResponse,
} from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Dialog } from "@radix-ui/react-dialog";
import {
    EnvironmentClass,
    type HostAuthenticationTokenSource,
    RunnerKind,
    type HostAuthenticationToken,
    type SCMIntegration,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase, type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import { CheckIcon, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { DESKTOP_APP_DOWNLOAD_URL } from "@/desktop";
import { hostAuthenticationTokenSourceLabel } from "@/utils/host-authentication-token-source";

type RunnerTokenBase = {
    entryId: string;
    scmHost: string;
    runner: PlainMessage<Runner>;
};
type RunnerTokenLocal = RunnerTokenBase & {
    token: PlainMessage<HostAuthenticationToken>;
    integration: undefined;
};

type RunnerTokenRemote = RunnerTokenBase & {
    token: PlainMessage<HostAuthenticationToken> | undefined;
    integration: PlainMessage<SCMIntegration>;
};

type RunnerToken = RunnerTokenLocal | RunnerTokenRemote;

export type GitAuthenticationsPageProps = {
    /** When true, hides the description text (used in modal context where description is provided externally) */
    hideDescription?: boolean;
};

export const GitAuthenticationsPage: FC<GitAuthenticationsPageProps> = ({ hideDescription = false }) => {
    useDocumentTitle("Git Authentications");
    const { data: tokensData, error: tokensError, isPending: isPendingTokens } = useListHostAuthenticationTokens({});
    const { data: runners, isPending: isPendingRunners } = useListRunners({ kind: RunnerKind.REMOTE });
    const { data: user, isPending: isPendingUser } = useAuthenticatedUser();
    const { data: localRunners, isPending: isPendingLocalRunners } = useListRunners({
        kind: RunnerKind.LOCAL,
        creatorId: user?.id,
    });

    const allRunners = useMemo(() => {
        return [...(runners || []), ...(localRunners || [])];
    }, [runners, localRunners]);

    const { data: integrations, isPending: integrationsPending } = useListAllRunnerSCMIntegrations(
        allRunners.map((runner) => runner.runnerId),
    );

    const tokens: RunnerToken[] = useMemo(
        () => runnerTokens(tokensData?.tokens || [], allRunners, integrations || []),
        [tokensData, allRunners, integrations],
    );

    const isLoading =
        isPendingTokens || isPendingRunners || isPendingUser || isPendingLocalRunners || integrationsPending;

    if (isLoading) {
        return <LoadingState />;
    }

    return (
        <div data-testid="git-authentications">
            <ErrorMessage error={tokensError} />
            <div className={hideDescription ? "" : "flex flex-col gap-6"}>
                {!hideDescription && (
                    <Text className="text-base text-content-secondary">
                        Git authentication connects Ona to your source control accounts. This page shows your current
                        GitHub, Bitbucket Cloud, or Azure DevOps connections for both runners and Ona Desktop.
                    </Text>
                )}
                {!tokensError && (
                    <>
                        {tokens.length === 0 && (
                            <EmptyStateCard
                                title="No Git authentications"
                                description="You need to add a runner or install Ona Desktop before connecting to your source control accounts."
                                data-testid="no-runner-configured-content"
                            >
                                <Button variant="secondary" asChild>
                                    <Link to="/settings/runners" data-testid="runner-onboarding-link">
                                        Set up a Runner
                                    </Link>
                                </Button>

                                <Button variant="secondary" asChild LeadingIcon={AppleLogo}>
                                    <ExternalLink
                                        href={DESKTOP_APP_DOWNLOAD_URL}
                                        download
                                        data-testid="runner-onboarding-link"
                                    >
                                        Download Ona Desktop
                                    </ExternalLink>
                                </Button>
                            </EmptyStateCard>
                        )}
                        {tokens.length > 0 && (
                            <div className={hideDescription ? "" : "overflow-auto"}>
                                <table className={`w-full divide-y divide-border-base ${hideDescription ? "" : "min-w-[800px]"}`}>
                                    <thead>
                                        <tr className="border-b border-border-light bg-surface-primary">
                                            <th className={`px-4 py-3 text-left text-base font-medium text-content-strong ${hideDescription ? "w-[30%]" : "w-[25%]"}`}>
                                                Runner
                                            </th>
                                            <th className="w-[15%] px-4 py-3 text-left text-base font-medium text-content-strong">
                                                Status
                                            </th>
                                            <th className={`px-4 py-3 text-left text-base font-medium text-content-strong ${hideDescription ? "w-[20%]" : "w-[25%]"}`}>
                                                Git Provider
                                            </th>
                                            <th className={`px-4 py-3 text-left text-base font-medium text-content-strong ${hideDescription ? "w-[15%]" : "w-[20%]"}`}>
                                                Auth type
                                            </th>
                                            <th className={`px-4 py-3 text-right text-base font-medium text-content-strong ${hideDescription ? "w-[20%]" : "w-[15%]"}`} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tokens.map((rt) => (
                                            <GitHostAuthenticationToken key={rt.entryId} token={rt} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const GitHostAuthenticationToken: FC<{
    token: RunnerToken;
}> = ({ token }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAuthenticateModal, setShowAuthenticateModal] = useState(false);
    const {
        data: authCheckResponse,
        isLoading: isAuthCheckLoading,
        refetch: refetchAuthCheck,
    } = useIsUserAuthenticatedWithRunner(token.runner.runnerId, "https://" + token.scmHost, {
        refetchUntilAuthenticated: false,
        enabled: true,
    });

    const handleConnectClick = useCallback(async () => {
        await refetchAuthCheck();
        setShowAuthenticateModal(true);
    }, [refetchAuthCheck, setShowAuthenticateModal]);

    return (
        <tr className="border-b border-border-light" data-testid={`git-authentications-token-${token.entryId}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <Tooltip content={token.runner.name}>
                        <span className="block min-w-0 max-w-full truncate text-base font-medium">
                            {token.runner.name}
                        </span>
                    </Tooltip>
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="truncate">
                    <RunnerPhaseTag
                        runner={token.runner}
                        labelOverrides={{
                            [RunnerPhase.ACTIVE]: "Active",
                        }}
                    />
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2 truncate">
                    <SourceControlProviderIcon scmId={token.integration?.scmId} size="sm" />
                    <span className="truncate text-base font-medium">{token.scmHost}</span>
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="truncate">
                    {token.token && <HostAuthenticationTokenSourceView source={token.token.source} />}
                </div>
            </td>
            <td className="px-4 py-3 text-right">
                {token.token ? (
                    <Button
                        variant="ghost"
                        onClick={() => setShowDeleteModal(true)}
                        data-testid={`git-authentications-delete-token-${token.entryId}`}
                        data-tracking-id="disconnect-git-host-authentication-token"
                    >
                        Disconnect
                    </Button>
                ) : (
                    <Button
                        variant="text"
                        onClick={handleConnectClick}
                        disabled={isAuthCheckLoading}
                        data-testid={`git-authentications-create-token-${token.entryId}`}
                        data-tracking-id="connect-git-host-authentication-token"
                    >
                        {isAuthCheckLoading ? "Checking..." : "Connect"}
                    </Button>
                )}
                {showDeleteModal && token.token && (
                    <HostAuthenticationTokenDeleteModal
                        token={{ ...token, token: token.token }}
                        onClose={() => setShowDeleteModal(false)}
                    />
                )}
                {showAuthenticateModal && authCheckResponse && (
                    <HostAuthenticationTokenAuthenticateModal
                        token={token}
                        authCheckResponse={authCheckResponse}
                        onClose={() => setShowAuthenticateModal(false)}
                    />
                )}
            </td>
        </tr>
    );
};

const HostAuthenticationTokenAuthenticateModal: FC<{
    token: RunnerToken;
    authCheckResponse: AuthenticatedWithRunnerResponse;
    onClose: () => void;
}> = ({ token, onClose, authCheckResponse }) => {
    const { toast } = useToast();

    const clazz = useMemo(() => {
        return toPlainRunnerEnvironmentClass(
            new EnvironmentClass({ displayName: "", runnerId: token.runner.runnerId }),
        );
    }, [token.runner.runnerId]);

    return (
        <AuthenticationModal
            repoURL={"https://" + token.scmHost}
            authResponse={authCheckResponse}
            runnerId={clazz.runnerId}
            onAuthSuccess={() => {
                toast({ title: "Authentication successful" });
                onClose();
            }}
            onClose={() => {
                onClose();
            }}
            data-track-location={TrackLocations.GitAuthenticationTokenCreateModal}
        />
    );
};

const HostAuthenticationTokenDeleteModal: FC<{
    token: RunnerToken & { token: NonNullable<RunnerToken["token"]> };
    onClose: () => void;
}> = ({ token, onClose }) => {
    const { toast } = useToast();
    const deleteToken = useDeleteHostAuthenticationToken();
    const listEnvironments = useRunnerEnvironments(token.token.runnerId);

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleDeleteToken = useCallback(() => {
        deleteToken.mutate(
            { tokenId: token.token.id },
            {
                onSuccess: () => {
                    toast({ title: "Authentication removed" });
                    onClose();
                },
                onError: (err) => {
                    toast({ title: "Failed to remove authentication", description: formatError(err) });
                },
            },
        );
    }, [deleteToken, onClose, token, toast]);

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-[400px]"
                data-testid={`git-authentications-delete-token-modal-${token.entryId}`}
                data-track-location={TrackLocations.GitAuthenticationTokenDeleteModal}
            >
                <DialogHeader>
                    <DialogTitle>Remove authentication</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4">
                    <Text>
                        Remove your access to {token.token.host} for{" "}
                        <span className="font-bold">{token.runner.name}</span> runner?
                    </Text>
                    <div className="mb-1 flex rounded-lg border border-border-light bg-surface-secondary p-4 text-content-secondary">
                        <AffectedEnvironmentsText
                            token={token}
                            isLoading={listEnvironments.isLoading || listEnvironments.isPending}
                            environmentCount={listEnvironments.data?.environments.length ?? 0}
                            error={listEnvironments.error}
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-remove-git-authentication-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="destructive"
                        disabled={listEnvironments.isLoading || listEnvironments.isPending}
                        loading={deleteToken.isPending}
                        onClick={handleDeleteToken}
                        data-testid={`git-authentications-delete-token-modal-${token.entryId}-remove`}
                        data-tracking-id="confirm-remove-git-authentication-modal"
                    >
                        Yes, Remove
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const AffectedEnvironmentsText: FC<{
    token: RunnerToken;
    isLoading: boolean;
    environmentCount: number;
    error: Error | null;
}> = ({ token, isLoading, environmentCount, error }) => {
    let icon: React.ReactNode;
    let content: string;
    const hostName = token.token?.host || token.scmHost;

    if (isLoading) {
        icon = <Loader2 strokeWidth={2} className="animate-spin text-content-yield" size={20} />;
        content = "Verifying affected environments...";
    } else if (error) {
        icon = <IconWarning size={"base"} className="text-content-negative" />;
        content = `There was an error while verifying affected environments. Proceed with caution. Any existing environments will be unable to communicate with ${hostName} until you re-authenticate.`;
    } else if (environmentCount == 0) {
        icon = <CheckIcon size={20} className="text-content-positive" />;
        content = "Safe to remove.";
    } else {
        icon = <IconWarning size={"base"} className="text-content-yield" />;
        content = `${environmentCount} environment${environmentCount > 1 ? "s" : ""} will be unable to communicate with ${hostName} until you re-authenticate.`;
    }

    return (
        <div className="inline-flex items-start justify-start gap-2">
            <div className="flex size-5 items-center">{icon}</div>
            <Text className="text-content-secondary">{content}</Text>
        </div>
    );
};

const HostAuthenticationTokenSourceView: FC<{ source: HostAuthenticationTokenSource }> = ({ source }) => {
    const text = hostAuthenticationTokenSourceLabel(source);
    return <span className="text-base text-content-secondary">{text}</span>;
};

function runnerTokens(
    tokens: PlainMessage<HostAuthenticationToken>[],
    runners: PlainMessage<Runner>[],
    integrations: PlainMessage<SCMIntegration>[],
): RunnerToken[] {
    const runnerById = Object.fromEntries(runners.map((runner) => [runner.runnerId, runner]));
    const tokenByRunnerIdAndHost = Object.fromEntries(
        tokens.map((token) => [`${token.runnerId}-${token.host}`, token]),
    );
    const integrationByRunnerIdAndHost = Object.fromEntries(
        integrations.map((integration) => [`${integration.runnerId}-${integration.host}`, integration]),
    );
    // Add remote runner tokens, they're re-authenticatable
    const values: RunnerToken[] = [];
    for (const integration of integrations) {
        const runner = runnerById[integration.runnerId] || undefined;
        const token = tokenByRunnerIdAndHost[`${integration.runnerId}-${integration.host}`] || undefined;
        if (runner) {
            values.push({
                entryId: integration.id,
                scmHost: integration.host,
                token,
                runner,
                integration,
            });
        }
    }
    // Add local runner tokens, not re-authenticatable on git authentications settings page
    for (const token of tokens) {
        const runner = runnerById[token.runnerId] || undefined;
        if (!runner || runner.kind === RunnerKind.REMOTE) {
            continue;
        }
        const integration = integrationByRunnerIdAndHost[`${token.runnerId}-${token.host}`] || undefined;
        values.push({
            entryId: token.id,
            scmHost: token.host,
            token,
            runner,
            integration,
        });
    }
    return values;
}
